import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { fmtPrice, withWatermark } from "@/lib/utils";
import { Link } from "react-router-dom";
import { Flame, MapPin, ArrowRight, TrendingDown } from "lucide-react";

export default function BonnesAffairesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/annonces/deals`, { params: { min_drop: 10, days: 30, limit: 24 } })
      .then((r) => setItems(r.data?.items || []))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="deals-page">
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-rose-100 via-orange-50 to-amber-50 border border-orange-200 px-8 py-10">
        <div className="absolute -top-10 -right-10 w-72 h-72 bg-orange-300/30 rounded-full blur-3xl pointer-events-none" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-white text-rose-600 px-3 py-1 rounded-full border border-rose-200">
            <Flame size={12} /> Mises a jour quotidiennes
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight mt-3">
            Bonnes affaires <span className="text-rose-500">-10% et plus</span>
          </h1>
          <p className="text-[var(--ink-soft)] mt-3 max-w-2xl">
            Toutes les annonces dont le prix a baisse d&apos;au moins 10% au cours des 30 derniers jours,
            classees par plus forte reduction. Les vendeurs motives sont en tete.
          </p>
        </div>
      </section>

      <section className="mt-8">
        {loading && <div className="text-center py-12 text-[var(--ink-soft)]">Chargement des bonnes affaires...</div>}
        {error && <div className="text-center py-12 text-red-600 font-semibold" data-testid="deals-error">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="glass-card p-12 text-center" data-testid="deals-empty">
            <div className="font-bold text-lg mb-2">Aucune bonne affaire pour le moment</div>
            <p className="text-sm text-[var(--ink-soft)] mb-5">Revenez bientot — les vendeurs ajustent leurs prix regulierement.</p>
            <Link to="/annonces" className="btn-primary">Parcourir toutes les annonces</Link>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="deals-grid">
            {items.map((a) => <DealCard key={a.id} a={a} />)}
          </div>
        )}
      </section>
    </main>
  );
}

function DealCard({ a }) {
  const img = (a.images && a.images[0]) || a.image || "/placeholder.png";
  return (
    <Link to={`/annonces/${a.id}`} className="group glass-card overflow-hidden flex flex-col hover:shadow-xl transition" data-testid={`deal-card-${a.id}`}>
      <div className="relative">
        <img src={withWatermark(img)} alt={a.title} className="w-full h-44 object-cover" />
        <span
          className="absolute top-2 left-2 inline-flex items-center gap-1 text-[11px] font-bold bg-rose-500 text-white px-2.5 py-1 rounded-full shadow"
          data-testid={`deal-badge-${a.id}`}
        >
          <TrendingDown size={11} /> -{a.drop_pct}%
        </span>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-bold text-sm line-clamp-2 group-hover:text-orange-600 transition">{a.title}</div>
        <div className="text-[11px] text-[var(--ink-soft)] mt-1 inline-flex items-center gap-1">
          <MapPin size={11} /> {a.city}{a.quartier ? ` · ${a.quartier}` : ""}
        </div>
        <div className="mt-2 flex items-end gap-2">
          <span className="font-extrabold text-orange-600 text-lg">{fmtPrice(a.price, a.price_unit)}</span>
          <span className="text-xs text-[var(--ink-soft)] line-through">{new Intl.NumberFormat("fr-FR").format(a.ref_price)} FCFA</span>
        </div>
        <div className="text-xs font-semibold text-rose-600 mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
          Voir l&apos;annonce <ArrowRight size={12} />
        </div>
      </div>
    </Link>
  );
}
